/**
 * Monitoring Configuration
 *
 * This module provides controls for toggling application monitoring systems.
 *
 * IMPORTANT: Changes to these settings ONLY take effect after the application
 * is restarted/refreshed. This is by design to avoid race conditions or
 * partial disabling of monitoring systems.
 *
 * CONFIGURATION FLAGS:
 * Set these flags to control monitoring systems.
 * These settings override any localStorage values.
 */

// SET TO FALSE TO DISABLE PERFORMANCE MONITORING
const ENABLE_PERFORMANCE_MONITORING = true;

// SET TO FALSE TO DISABLE REDUX SIZE MONITORING
const ENABLE_REDUX_SIZE_MONITORING = true;

// Track if we've already logged status (prevent duplicate notifications)
let hasLoggedPerformanceStatus = false;
let hasLoggedReduxStatus = false;

/**
 * Log a notification when a monitoring system is disabled
 * Only logs once per system per session
 */
function logDisabledStatus(system: string, isDisabled: boolean): void {
  if (!isDisabled) return; // Only log when disabled

  if (system === "performance" && !hasLoggedPerformanceStatus) {
    console.warn("⚠️ Performance Monitoring is DISABLED");
    hasLoggedPerformanceStatus = true;
  } else if (system === "redux" && !hasLoggedReduxStatus) {
    console.warn("⚠️ Redux Size Monitoring is DISABLED");
    hasLoggedReduxStatus = true;
  }
}

/**
 * Get status of the Performance Monitoring system
 * @returns boolean - true if enabled, false if disabled
 */
export function isPerformanceMonitoringEnabled(): boolean {
  // If the override flag is false, always return false
  if (!ENABLE_PERFORMANCE_MONITORING) {
    logDisabledStatus("performance", true);
    return false;
  }

  // Otherwise, check localStorage as before
  try {
    if (typeof localStorage !== "undefined") {
      const value = localStorage.getItem("ENABLE_PERFORMANCE_MONITORING");
      if (value !== null) {
        const isEnabled = value === "true";
        logDisabledStatus("performance", !isEnabled);
        return isEnabled;
      }
    }
  } catch (e) {
    // Ignore storage errors
  }
  return true; // Default to enabled
}

/**
 * Get status of the Redux Size Monitoring system
 * @returns boolean - true if enabled, false if disabled
 */
export function isReduxSizeMonitoringEnabled(): boolean {
  // If the override flag is false, always return false
  if (!ENABLE_REDUX_SIZE_MONITORING) {
    logDisabledStatus("redux", true);
    return false;
  }

  // Otherwise, check localStorage as before
  try {
    if (typeof localStorage !== "undefined") {
      const value = localStorage.getItem("ENABLE_REDUX_SIZE_MONITORING");
      if (value !== null) {
        const isEnabled = value === "true";
        logDisabledStatus("redux", !isEnabled);
        return isEnabled;
      }
    }
  } catch (e) {
    // Ignore storage errors
  }
  return true; // Default to enabled
}

/**
 * Enable or disable Performance Monitoring
 *
 * @param enable - true to enable, false to disable
 * @returns boolean - whether the operation succeeded
 *
 * NOTE: Changes only take effect after application restart/refresh.
 * This is intentional to avoid inconsistent state or timing issues.
 */
export function setPerformanceMonitoring(enable: boolean): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("ENABLE_PERFORMANCE_MONITORING", enable.toString());
      return true;
    }
  } catch (e) {
    console.error("Failed to set performance monitoring state:", e);
  }
  return false;
}

/**
 * Enable or disable Redux Size Monitoring
 *
 * @param enable - true to enable, false to disable
 * @returns boolean - whether the operation succeeded
 *
 * NOTE: Changes only take effect after application restart/refresh.
 * This is intentional to avoid inconsistent state or timing issues.
 */
export function setReduxSizeMonitoring(enable: boolean): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("ENABLE_REDUX_SIZE_MONITORING", enable.toString());
      return true;
    }
  } catch (e) {
    console.error("Failed to set Redux size monitoring state:", e);
  }
  return false;
}

/**
 * Get the current status of all monitoring systems
 *
 * @returns object with status of each monitoring system
 */
export function getMonitoringStatus(): {
  performanceMonitoring: boolean;
  reduxSizeMonitoring: boolean;
} {
  return {
    performanceMonitoring: isPerformanceMonitoringEnabled(),
    reduxSizeMonitoring: isReduxSizeMonitoringEnabled(),
  };
}

/**
 * Reset all monitoring settings to defaults (enabled)
 *
 * @returns boolean - whether the operation succeeded
 *
 * NOTE: Changes only take effect after application restart/refresh.
 */
export function resetMonitoringSettings(): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("ENABLE_PERFORMANCE_MONITORING");
      localStorage.removeItem("ENABLE_REDUX_SIZE_MONITORING");
      return true;
    }
  } catch (e) {
    console.error("Failed to reset monitoring settings:", e);
  }
  return false;
}

/**
 * Instructions for using these functions from the browser console:
 *
 * // To disable performance monitoring:
 * import('utils/monitoringConfig').then(m => m.setPerformanceMonitoring(false))
 * // Then refresh the page
 *
 * // To disable Redux size monitoring:
 * import('utils/monitoringConfig').then(m => m.setReduxSizeMonitoring(false))
 * // Then refresh the page
 *
 * // To check current status:
 * import('utils/monitoringConfig').then(m => console.log(m.getMonitoringStatus()))
 *
 * // To reset all to defaults:
 * import('utils/monitoringConfig').then(m => m.resetMonitoringSettings())
 * // Then refresh the page
 */

// Export a simplified monitoring config object for use in other modules
export const MonitoringConfig = {
  ENABLE_PERFORMANCE_MONITORING: isPerformanceMonitoringEnabled(),
  ENABLE_REDUX_SIZE_MONITORING: isReduxSizeMonitoringEnabled(),
};

export default MonitoringConfig;
